import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureMessage } from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useGetDetailedExtractionStatsByPeriodId } from '../../../../services/funds.service';
import {
  useAutomateReExtraction,
  useCancelAutomateExtraction
} from '../../../../services/automation.service';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { TbReload, TbSearch } from 'react-icons/tb';
import { IoClose } from 'react-icons/io5';
import { GoArrowUpRight } from 'react-icons/go';
import { Tooltip } from 'react-tooltip';
import {
  Container,
  IssuesEmpty,
  IssuesTable,
  IssuesToolbar,
  HeadCell,
  StatusChip,
  ErrorMessageStyle,
  DescriptionStyle,
  FileNameStyle,
  OpenSheetLink,
  ProgressCellStyle,
  ConfidenceButtonStyle,
  customStyles,
  colors,
  Sheet,
  Backdrop
} from './styles';
import { AiExtractionDetails } from './AiExtractionDetails';
import agentStore from '../../../../store/agent';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { WEB_PAGE_STATE_ENUMS } from '../../../../components/ChatWidget/constants';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import {
  ActionIcon,
  Button as MantineButton,
  /* The one the card list already selects with — the legacy checkbox is a 16px div with its own
     blue and its own tick, and beside V2's controls it read as a leftover. */
  Checkbox,
  Menu,
  ScrollArea,
  TextInput
} from '@mantine/core';
import { TbChevronDown, TbX } from 'react-icons/tb';
import { ControlButton } from '../../../../components/v2';
import { DX, DX_ROOT_CLASS } from '../../../../theme/v2';
import { DataTable } from '../../../../components/v2/DataTable';
import { SortButton } from '../../components/SortButton';
import { TRANSFORMATION_TYPES } from '../../../../consts/transformationTypes';
import { useSignalRListener } from '../../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../../config/signalr';

/* Columns whose value has to be read whole: the cell wraps and the row grows with it. */
import { useExtractionProgress } from '../../../../hooks/useExtractionProgress';
import { formatElapsed } from '../../../../utils/extractionProgress';
import { debug } from '../../../../services/logger';

const WRAP = { 'data-wrap': 'true' };

export const ExtractionIssuesV2 = ({
  /* The list is empty because the work has only just been asked for, not because it came back… */
  isStarting = false,
  isOpen,
  onClose,
  periodId,
  worklowId,
  refetch,
  isLatestPeriod,
  canManageWorkflow = true
}) => {
  const navigate = useNavigate();
  const automateReExtraction = useAutomateReExtraction();
  const cancelAutomateExtraction = useCancelAutomateExtraction();

  const [selectedExtraction, setSeletectedExtraction] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedConfidenceRecord, setSelectedConfidenceRecord] = useState(null);
  const [resettingConfidenceBySheet, setResettingConfidenceBySheet] = useState({});
  const categoryOptionsRef = useRef([]);
  /* State, not a ref. react-query recomputes a query's options when something re-renders it, and a
     ref re-renders nothing — so a safety net gated on one is never deployed: the interval is read
     once, as false, and stays false however many tasks start afterwards. That is why a row could
     sit at "Queued" until the panel was closed and opened again. */
  const [watchUnsettled, setWatchUnsettled] = useState(false);

  const apiParams = useMemo(() => {
    const p = {};
    if (debouncedSearch) p.search_query = debouncedSearch;
    if (selectedCategory) p.category_name = selectedCategory.name;
    if (sortConfig.key) p.sort_by = sortConfig.direction;
    return p;
  }, [debouncedSearch, selectedCategory, sortConfig]);

  const {
    refetch: refetchIssues,
    data: issues,
    isLoading,
    isError,
    error
  } = useGetDetailedExtractionStatsByPeriodId({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen,
      /* SignalR is the fast path and usually wins the race; this is what stops the table from
         freezing when the push never lands — a hub that gave up after its retries ran out, a
         message dropped by the environment filter, a backend that did not send one. It costs one
         request every ten seconds and only while something is actually running. */
      refetchInterval: watchUnsettled ? 10000 : false
    }
  });

  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, data => {
    // TODO: Once confirmed that extraction status update works remove this sentry log
    captureMessage('SignalR ON_AUTOMATION_EXTRACTION_STATUS_UPDATED', {
      tags: {
        service: 'signalr-service'
      },
      extra: {
        dataFromBackend: data,
        dataFromFrontend: {
          isOpen,
          periodId
        }
      }
    });
    debug('ExtractionIssues.signalr', { data, periodId, isOpen });

    // Check if the notification is for the current period
    // Environment filtering is handled automatically by useSignalRListener hook
    if (data.period_id === periodId && isOpen) {
      debug('ExtractionIssues.signalr', 'period match → refetching');
      refetchIssues();
    }
  });

  const onAutoExtract = () => {
    const resettingRows = selectedExtraction.reduce((acc, sheetMappingId) => {
      const record = (issues ?? []).find(item => item.sheet_mapping_id === sheetMappingId);
      acc[sheetMappingId] = record?.latest_automation_task?.id ?? null;
      return acc;
    }, {});
    setResettingConfidenceBySheet(prev => ({ ...prev, ...resettingRows }));

    automateReExtraction.mutate(
      {
        period_id: periodId,
        sheet_mapping_ids: selectedExtraction
      },
      {
        onSuccess: () => {
          setSeletectedExtraction([]);
          refetch();
          refetchIssues();
          toast.success('Automated Extraction have been successfully applied', {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const onCancelExtraction = () => {
    cancelAutomateExtraction.mutate(
      {
        task_ids: selectedExtraction
          .map(
            item =>
              (issues ?? []).find(value => value.sheet_mapping_id === item)?.latest_automation_task
                ?.id
          )
          .filter(Boolean)
      },
      {
        onSuccess: () => {
          setSeletectedExtraction([]);
          refetch();
          refetchIssues();
          toast.success('Automated Extraction has been successfully cancelled', {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const onSelectExtraction = id => {
    if (selectedExtraction.includes(id)) {
      setSeletectedExtraction(prev => prev.filter(value => value !== id));
    } else {
      setSeletectedExtraction(prev => [...prev, id]);
    }
  };

  /* Into the sheet, in the design this drawer belongs to. The redesigned extraction page keys its
     state off `fileId` and `sheetId` — the same two this has always sent — so the row opens on the
     sheet it named either way. */
  const onNavigate = (fileId, sheetId, category, extractionId) => {
    const query = `?fileId=${fileId}&sheetId=${sheetId}${category ? `&category=${category}` : ''}${extractionId ? `&extractId=${extractionId}` : ''}`;
    navigate(`/data-extraction/${worklowId}/${periodId}${query}`);
  };

  const isAllSelected = useMemo(() => {
    const selectable = issues?.filter(
      item => item?.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL
    );
    if (!selectable?.length) return false;
    return selectable.every(item => selectedExtraction.includes(item.sheet_mapping_id));
  }, [selectedExtraction, issues]);

  const toggleAllExtractions = () => {
    if (isAllSelected) {
      setSeletectedExtraction([]);
    } else {
      setSeletectedExtraction(
        issues
          .filter(item => item.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL)
          .map(item => item.sheet_mapping_id)
      );
    }
  };

  const categoryOptions = useMemo(() => {
    if (!issues?.length) {
      categoryOptionsRef.current = [];
      return categoryOptionsRef.current;
    }
    if (!selectedCategory) {
      const unique = [...new Set(issues.map(item => item.category).filter(Boolean))];
      categoryOptionsRef.current = unique
        .sort()
        .map(category => ({ id: category, name: category }));
    }
    return categoryOptionsRef.current;
  }, [issues, selectedCategory]);

  const tableData = issues ?? [];

  const taskOfRecord = useCallback(record => record?.latest_automation_task, []);
  const displayTaskStatus = status =>
    status === 'Queued' || status === 'QUEUED' ? 'In progress' : status;

  /* How far along each running row is, and how long it has been running. Both come from what the
     backend reports; neither is invented here. See utils/extractionProgress. */
  const { progressOf, hasPendingTask } = useExtractionProgress(issues, {
    isActive: isOpen,
    taskOf: taskOfRecord
  });

  /* Queued counts. A task the server has accepted but not started is exactly the case this net is
     for — it reports no progress and sends no push until it begins, so without this the table shows
     the word "Queued" and nothing ever comes to replace it. */
  useEffect(() => {
    setWatchUnsettled(hasPendingTask);
  }, [hasPendingTask]);

  const getConfidenceScore = record => {
    const taskStatus = displayTaskStatus(record.latest_automation_task?.status);
    if (['Failed', 'Cancelled', 'Not started', 'In progress'].includes(taskStatus)) return null;

    const confidenceScore = record.confidence_score;
    if (typeof confidenceScore !== 'number') return null;
    return Math.min(100, Math.max(0, confidenceScore));
  };

  /* What did not extract comes first: it is the reason this opened. */
  const orderedData = [...tableData].sort((left, right) => {
    const rank = record => ((record.progress_percentage ?? 100) < 100 ? 0 : 1);
    return rank(left) - rank(right);
  });

  /* Every row says which file it came from, at the same weight as every other row. Grouping the
     runs — a blank continuation cell, or the repeat a shade back — costs more than it buys: the
     usual period is one workbook across all its sheets, so there is no run to tell apart, and the
     one row left at full strength reads as singled out rather than as the head of a group. */

  const columns = [
    {
      key: 'file_name',
      title: 'File name',
      tdProps: WRAP,
      /* The widest of them, because it is the longest string in the table and the one you read to
         know which row you are on. */
      thStyle: { width: 340 },
      renderHeadCell: () => {
        return (
          <HeadCell>
            {isLatestPeriod && (
              <Checkbox
                checked={isAllSelected}
                /* Some of them, not all: the head says so rather than showing an empty box while
                   four rows below it are ticked. */
                indeterminate={selectedExtraction.length > 0 && !isAllSelected}
                onChange={toggleAllExtractions}
                aria-label="Select all sheets"
                data-testid="extraction-checkbox-all"
              />
            )}
            File name
            <SortButton
              columnName="file_name"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </HeadCell>
        );
      },
      render: record => {
        const isManually = record?.automation_extraction_type === TRANSFORMATION_TYPES.MANUAL;
        return (
          <FileNameStyle title={record.file_name}>
            {isLatestPeriod && (
              <Checkbox
                disabled={isManually}
                checked={selectedExtraction.includes(record.sheet_mapping_id)}
                onChange={() => onSelectExtraction(record.sheet_mapping_id)}
                aria-label={`Select ${record.sheet_name}`}
                data-testid={`extraction-checkbox-${record.sheet_mapping_id}`}
              />
            )}
            <span>{record.file_name}</span>
          </FileNameStyle>
        );
      }
    },
    {
      key: 'sheet_name',
      title: 'Sheet name',
      thStyle: { width: 190 },
      tdProps: WRAP,
      renderHeadCell: () => (
        <HeadCell>
          Sheet name
          <SortButton
            columnName="sheet_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </HeadCell>
      )
    },
    {
      key: 'table_structure',
      title: 'Table structure',
      /* Wide enough for its own caption: 'Table structure' was the one header the table clipped. */
      thStyle: { width: 140 },
      tdProps: WRAP,
      render: record =>
        record.is_structured == null ? '-' : record.is_structured ? 'Structured' : 'Unstructured'
    },
    {
      key: 'category',
      title: 'Category',
      thStyle: { width: 150 },
      tdProps: WRAP,
      renderHeadCell: () => (
        <HeadCell>
          Category
          <SortButton
            columnName="category_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </HeadCell>
      )
    },
    {
      key: 'status',
      title: 'Status',
      thStyle: { width: 150 },
      render: record => {
        const isManually = record?.automation_extraction_type === TRANSFORMATION_TYPES.MANUAL;
        const label = isManually
          ? TRANSFORMATION_TYPES.MANUAL
          : displayTaskStatus(record.latest_automation_task?.status) || 'Not extracted';
        const progress = progressOf(record);
        /* Only the state that moves says how far it has got: "Completed" already means a hundred
           per cent, and "Not extracted" already means none. */
        const isRunning = !isManually && progress.isRunning;
        const rowId = record.sheet_mapping_id || record.sheet_id;
        const elapsed = formatElapsed(progress.elapsedMs);
        /* The backend's number when there is one and the run still looks healthy. Otherwise how
           long it has been going: a row that has been at it for twenty minutes says so, instead of
           saying 95% like every other row that ever ran. */
        const readout =
          progress.percentage != null && !progress.isSlow ? `${progress.percentage}%` : elapsed;

        return (
          <ProgressCellStyle id={`extraction-progress-${rowId}`}>
            <StatusChip
              $tone={isRunning && progress.isSlow ? DX.tone.warning : colors[label]}
              $progress={isRunning ? progress.percentage : null}
              $indeterminate={isRunning && progress.isIndeterminate}
              data-slow={isRunning && progress.isSlow ? 'true' : undefined}
              data-tooltip-id={isRunning && progress.isSlow ? 'warning-tooltip' : undefined}
              data-tooltip-content={
                isRunning && progress.isSlow
                  ? `Running for ${elapsed}, longer than extraction usually takes. If it does not finish, cancel it and re-extract.`
                  : undefined
              }
            >
              <i className="mark" aria-hidden />
              {label}
              {isRunning && readout && (
                <span className="pct" id={`extraction-progress-text-${rowId}`}>
                  {readout}
                </span>
              )}
            </StatusChip>
          </ProgressCellStyle>
        );
      }
    },
    {
      key: 'confidence_score',
      title: 'Confidence score',
      thStyle: { width: 116 },
      render: record => {
        const resetPreviousTaskId = resettingConfidenceBySheet[record.sheet_mapping_id];
        const isResettingConfidence = Object.prototype.hasOwnProperty.call(
          resettingConfidenceBySheet,
          record.sheet_mapping_id
        );
        const currentTask = record.latest_automation_task;
        const shouldHideConfidence =
          isResettingConfidence &&
          (currentTask?.id === resetPreviousTaskId ||
            displayTaskStatus(currentTask?.status) === 'In progress' ||
            typeof record.confidence_score !== 'number');
        const confidenceScore = shouldHideConfidence ? null : getConfidenceScore(record);
        const isVerificationPending =
          confidenceScore == null &&
          displayTaskStatus(record.latest_automation_task?.status) === 'In progress';
        const isVerifierFinishing = shouldHideConfidence && currentTask?.status === 'Completed';
        const isScorePending =
          confidenceScore == null &&
          !isVerificationPending &&
          !isVerifierFinishing &&
          displayTaskStatus(currentTask?.status) === 'Completed' &&
          !!record.ai_extraction_details;

        return (
          <ConfidenceButtonStyle
            id={`extraction-confidence-${record.sheet_mapping_id || record.sheet_id}`}
            type="button"
            $score={confidenceScore}
            disabled={confidenceScore == null || !record.ai_extraction_details}
            title={
              confidenceScore == null && !record.ai_extraction_details
                ? 'This sheet was not extracted by AI, so it carries no confidence score'
                : undefined
            }
            onClick={() => {
              setSelectedConfidenceRecord(record);
            }}
          >
            {confidenceScore == null
              ? isVerificationPending
                ? 'Pending'
                : isVerifierFinishing || isScorePending
                  ? 'Verifying'
                  : '—'
              : `${confidenceScore.toFixed(1)}%`}
          </ConfidenceButtonStyle>
        );
      }
    },
    {
      key: 'issue_description',
      title: 'Issue description',
      tdProps: WRAP,
      /* Stated like every other column so the slack the sheet has left over is shared out across
         all of them — an unstated last column swallowed 400px and stood empty. It asks for more
         than it did: it carries whole warnings now, not the word "Warnings". */
      thStyle: { width: 260 },
      /* Every warning, in full, one per line — the same as the transformation table beside it. The
         column used to say "Warnings" and keep the rest in a tooltip, which is a fine thing to do
         to a word and a poor thing to do to the reason a sheet did not extract. */
      render: record => {
        const warnings = record.latest_automation_task?.automation_extraction_task_warnings || [];
        const taskError = record.latest_automation_task?.error_message;
        const hasErrorAndWarning = warnings.length > 0;
        const messages = warnings.length
          ? warnings.map(item => item.message)
          : taskError
            ? [taskError]
            : [];

        return (
          <DescriptionStyle>
            {messages.length > 0 && (
              <>
                <ErrorMessageStyle data-tone={warnings.length > 0 ? 'warning' : undefined}>
                  <span>
                    {messages.map((text, index) => (
                      <span key={`${text}-${index}`} className="line">
                        {messages.length > 1 ? `${index + 1}. ${text}` : text}
                      </span>
                    ))}
                  </span>
                </ErrorMessageStyle>

                <OpenSheetLink
                  onClick={() =>
                    onNavigate(
                      record.file_id,
                      record.sheet_id,
                      record.category,
                      record.extraction_id
                    )
                  }
                >
                  Open sheet
                  <GoArrowUpRight size={13} />
                </OpenSheetLink>
              </>
            )}

            {(!record.latest_automation_task ||
              (record.latest_automation_task?.status === 'Completed' && !hasErrorAndWarning)) && (
              <>
                <OpenSheetLink
                  onClick={() =>
                    onNavigate(
                      record.file_id,
                      record.sheet_id,
                      record.category,
                      record.extraction_id
                    )
                  }
                >
                  Open sheet
                  <GoArrowUpRight size={13} />
                </OpenSheetLink>
              </>
            )}
          </DescriptionStyle>
        );
      }
    }
  ];
  useDidUpdate(() => {
    setSeletectedExtraction([]);
  }, [debouncedSearch, selectedCategory, sortConfig]);

  useDidUpdate(() => {
    if (isOpen) {
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.AutomationExtractionDashboard);
    } else {
      agentStore.setCurrentSubScreen('');
      setSeletectedExtraction([]);
      setSortConfig({ key: '', direction: '' });
      setSearchTerm('');
      setSelectedCategory(null);
      setSelectedConfidenceRecord(null);
      setResettingConfidenceBySheet({});
    }
  }, [isOpen]);

  useDidUpdate(() => {
    if (!Object.keys(resettingConfidenceBySheet).length) return;

    setResettingConfidenceBySheet(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(prev).forEach(([sheetMappingId, previousTaskId]) => {
        const record = (issues ?? []).find(
          item => String(item.sheet_mapping_id) === String(sheetMappingId)
        );
        const currentTask = record?.latest_automation_task;
        const taskChanged = currentTask?.id !== previousTaskId;
        const isFinished = ['Completed', 'Failed', 'Cancelled'].includes(currentTask?.status);
        const hasNewConfidence = typeof record?.confidence_score === 'number';

        if (
          (taskChanged && isFinished && hasNewConfidence) ||
          currentTask?.status === 'Failed' ||
          currentTask?.status === 'Cancelled'
        ) {
          delete next[sheetMappingId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [issues, resettingConfidenceBySheet]);

  const isSelectedExtractionsInProgress = useMemo(() => {
    return selectedExtraction.some(el => {
      const status = (issues ?? []).find(rec => rec.sheet_mapping_id === el)?.latest_automation_task
        ?.status;

      return displayTaskStatus(status) === 'In progress';
    });
  }, [selectedExtraction, issues]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      /* Both are styled elements of our own so react-modal hangs its classes on them rather than on
         the global ones every legacy modal shares. */
      contentElement={(props, children) => <Sheet {...props}>{children}</Sheet>}
      overlayElement={(props, contentEl) => <Backdrop {...props}>{contentEl}</Backdrop>}
    >
      {/* Same component as the periods page opens, in V2's clothes: the shell, the toolbar, the
          table and the footer are the page's own, the logic underneath is untouched. */}
      <Container className={DX_ROOT_CLASS}>
        {/* One level: what this is, how to narrow it, what it can do, and the way out. */}
        <IssuesToolbar>
          <h3>Extraction issues</h3>
          <span className="spacer" />

          <TextInput
            placeholder="Search sheets"
            leftSection={<TbSearch size={15} color="var(--dx-text-faint)" />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="xs"
            w={248}
          />

          <Menu shadow="sm" width={220} zIndex={1100}>
            <Menu.Target>
              <ControlButton data-active={!!selectedCategory}>
                {selectedCategory ? selectedCategory.name : 'Category'}
                <TbChevronDown size={14} />
              </ControlButton>
            </Menu.Target>
            <Menu.Dropdown>
              <ScrollArea.Autosize mah={300}>
                <Menu.Item fz="13px" onClick={() => setSelectedCategory(null)}>
                  All categories
                </Menu.Item>
                {categoryOptions.map(option => (
                  <Menu.Item key={option.id} fz="13px" onClick={() => setSelectedCategory(option)}>
                    {option.name}
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>

          <span className="count">
            {selectedExtraction.length > 0
              ? `${selectedExtraction.length} selected`
              : `${tableData?.length || 0} sheet${tableData?.length === 1 ? '' : 's'}`}
          </span>

          {isLatestPeriod && selectedExtraction.length > 0 && isSelectedExtractionsInProgress && (
            <MantineButton
              size="xs"
              variant="default"
              leftSection={<IoClose fontSize="15px" />}
              disabled={automateReExtraction.isLoading || cancelAutomateExtraction.isLoading}
              onClick={onCancelExtraction}
            >
              Cancel extraction
            </MantineButton>
          )}

          {isLatestPeriod && (
            <PermissionGuard allowed={canManageWorkflow}>
              <MantineButton
                size="xs"
                leftSection={<TbReload fontSize="15px" />}
                onClick={onAutoExtract}
                disabled={
                  automateReExtraction.isLoading ||
                  cancelAutomateExtraction.isLoading ||
                  selectedExtraction.length === 0
                }
              >
                Re-extract
              </MantineButton>
            </PermissionGuard>
          )}

          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
            <TbX size={16} />
          </ActionIcon>
        </IssuesToolbar>

        <IssuesTable>
          <DataTable
            idPrefix="extraction-issues-table"
            columns={columns}
            data={orderedData}
            isLoading={isLoading || (isStarting && !orderedData.length)}
            /* A refused statistics job used to arrive here as "nothing came out wrong" — the one
               sentence you must never print when you do not know. It says what the server said. */
            empty={
              <IssuesEmpty>
                {isError
                  ? `The extraction statistics could not be read. ${error?.message || ''}`.trim()
                  : 'Nothing came out wrong here.'}
              </IssuesEmpty>
            }
          />
        </IssuesTable>

        <Tooltip
          id="warning-tooltip"
          data-testid="warning-tooltip"
          style={{ background: '#868E96', padding: '8px' }}
        />
      </Container>
      {/* The verifier's own account of one sheet — the same window the setup list opens, so a
          score means the same thing whichever table you clicked it in. */}
      <AiExtractionDetails
        record={selectedConfidenceRecord}
        score={selectedConfidenceRecord ? getConfidenceScore(selectedConfidenceRecord) : null}
        onClose={() => setSelectedConfidenceRecord(null)}
      />
    </Modal>
  );
};
